import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { appStartPath } from './utils/session';
import useProgram from './hooks/useProgram';

// Public pages stay statically imported (eager). react-snap prerenders these
// routes, and hydration needs their code in the main bundle synchronously —
// lazy-loading them would flash the Suspense fallback over the prerendered
// HTML before the chunk arrives.
import Landing from './pages/Landing';
import YmcaBasketball from './pages/YmcaBasketball';
import CoachLogin from './pages/CoachLogin';
import CoachRegister from './pages/CoachRegister';
import ParentLogin from './pages/ParentLogin';
import ParentRegister from './pages/ParentRegister';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import SmsProgram from './pages/SmsProgram';
import GettingStartedGuide from './pages/GettingStartedGuide';
// v2.8 F4: the public live-score page. Eager like the other public pages
// (no login, opened from a shared link); not prerendered by react-snap.
import Scoreboard from './pages/Scoreboard';

// Authenticated pages are code-split. They are never prerendered (the server
// serves them the empty app-shell), so they load as separate chunks on demand
// instead of bloating the bundle every public visitor downloads.
const CoachLayout = lazy(() => import('./components/CoachLayout'));
const CoachDashboard = lazy(() => import('./pages/CoachDashboard'));
const CoachProfile = lazy(() => import('./pages/CoachProfile'));
const Families = lazy(() => import('./pages/Families'));
const TeamRoster = lazy(() => import('./pages/TeamRoster'));
const Schedule = lazy(() => import('./pages/Schedule'));
const Payments = lazy(() => import('./pages/Payments'));
const Messages = lazy(() => import('./pages/Messages'));
const SignUp = lazy(() => import('./pages/SignUp'));
const Settings = lazy(() => import('./pages/Settings'));
const CoachHandout = lazy(() => import('./pages/CoachHandout')); // v2.7 US-3.3 (FEATURE_HANDOUT link)

// Public utility pages — not prerendered, so lazy is fine (no hydration over
// prerendered HTML to worry about).
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

const ParentLayout = lazy(() => import('./components/ParentLayout'));
const ParentDashboard = lazy(() => import('./pages/ParentDashboard'));
const ParentProfile = lazy(() => import('./pages/ParentProfile'));
const ParentChildren = lazy(() => import('./pages/ParentChildren'));
const ParentSchedule = lazy(() => import('./pages/ParentSchedule'));
const ParentPayments = lazy(() => import('./pages/ParentPayments'));
const ParentMessages = lazy(() => import('./pages/ParentMessages'));
const ParentWaiver = lazy(() => import('./pages/ParentWaiver')); // v2.5 F2
const AdminConsole = lazy(() => import('./pages/AdminConsole')); // v2.5 F4 (ADMIN_EMAILS)
const TeamChat = lazy(() => import('./pages/TeamChat')); // v2.8 F2 (FEATURE_CHAT)
const Albums = lazy(() => import('./pages/Albums')); // v2.8 F5 (FEATURE_ALBUMS), both roles

// Shown while a code-split authenticated chunk loads. Public routes are eager,
// so they never trigger this.
const RouteFallback = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#5A6070',
      fontSize: '14px',
    }}
  >
    Loading…
  </div>
);

// Payment pages exist only for Paid programs (v2.0 F3). Free programs are
// sent to their dashboard before any payment content renders; while the
// program is still loading nothing payment-related is shown either.
const PaidOnly = ({ dashboard, children }) => {
  const { user } = useAuth();
  const { loading, isFree } = useProgram(user?.programId);
  if (loading) return <RouteFallback />;
  if (isFree) return <Navigate to={dashboard} replace />;
  return children;
};

// Suspense lives HERE, around the lazy chunks only — never around the
// prerendered public routes. React's hydration expects `<!--$-->` comment
// markers wherever a Suspense boundary was server-rendered, and react-snap's
// DOM snapshot has none, so a boundary above the public pages made every
// hydration fail (Sentry HOOPHERO-CLIENT-2) and the whole tree re-render.
const Lazy = ({ children }) => <Suspense fallback={<RouteFallback />}>{children}</Suspense>;

// X-5: the installed app's start_url. Sends a signed-in user to their portal
// instead of the marketing page; works offline via the service worker shell.
const AppStart = () => {
  const [params] = useSearchParams();
  return <Navigate to={appStartPath(params.get('to'))} replace />;
};

const ProtectedCoachRoute = ({ children }) => {
  const { user, role, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!user || role !== 'coach') return <Navigate to="/coach/login" />;
  return <Lazy><CoachLayout>{children}</CoachLayout></Lazy>;
};

// v2.7 US-3.3: a coach page without the layout chrome, for printing.
const ProtectedCoachPrintRoute = ({ children }) => {
  const { user, role, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!user || role !== 'coach') return <Navigate to="/coach/login" />;
  return <Lazy>{children}</Lazy>;
};

// v2.5 AD8: the owner console. Coach session whose login said isAdmin;
// the server re-checks ADMIN_EMAILS on every request.
const ProtectedAdminRoute = ({ children }) => {
  const { user, role, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!user || role !== 'coach') return <Navigate to="/coach/login" />;
  if (!user.isAdmin) return <Navigate to="/coach/dashboard" replace />;
  return <Lazy>{children}</Lazy>;
};

const ProtectedParentRoute = ({ children }) => {
  const { user, role, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!user || role !== 'parent') return <Navigate to="/parent/login" />;
  return <Lazy><ParentLayout>{children}</ParentLayout></Lazy>;
};

function App() {
  return (
    <HelmetProvider>
      <Router>
    <AuthProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/ymca-basketball" element={<YmcaBasketball />} />
          <Route path="/coach/login" element={<CoachLogin />} />
          <Route path="/coach/register" element={<CoachRegister />} />
          <Route path="/coach/forgot-password" element={<Lazy><ForgotPassword role="coach" /></Lazy>} />
          <Route path="/coach/reset-password" element={<Lazy><ResetPassword role="coach" /></Lazy>} />
          <Route path="/parent/login" element={<ParentLogin />} />
          <Route path="/parent/register" element={<ParentRegister />} />
          <Route path="/parent/forgot-password" element={<Lazy><ForgotPassword role="parent" /></Lazy>} />
          <Route path="/parent/reset-password" element={<Lazy><ResetPassword role="parent" /></Lazy>} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/sms" element={<SmsProgram />} />
          <Route path="/getting-started" element={<GettingStartedGuide />} />
          <Route path="/s/:token" element={<Scoreboard />} />
          <Route path="/app" element={<AppStart />} />

          <Route path="/admin" element={<ProtectedAdminRoute><AdminConsole /></ProtectedAdminRoute>} />
          <Route path="/coach/dashboard" element={<ProtectedCoachRoute><CoachDashboard /></ProtectedCoachRoute>} />
          <Route path="/coach/profile" element={<ProtectedCoachRoute><CoachProfile /></ProtectedCoachRoute>} />
          <Route path="/coach/families" element={<ProtectedCoachRoute><Families /></ProtectedCoachRoute>} />
          <Route path="/coach/roster" element={<ProtectedCoachRoute><TeamRoster /></ProtectedCoachRoute>} />
          <Route path="/coach/schedule" element={<ProtectedCoachRoute><Schedule /></ProtectedCoachRoute>} />
          <Route path="/coach/payments" element={<ProtectedCoachRoute><PaidOnly dashboard="/coach/dashboard"><Payments /></PaidOnly></ProtectedCoachRoute>} />
          <Route path="/coach/messages" element={<ProtectedCoachRoute><Messages /></ProtectedCoachRoute>} />
          <Route path="/coach/handout" element={<ProtectedCoachPrintRoute><CoachHandout /></ProtectedCoachPrintRoute>} />
          <Route path="/coach/signup" element={<ProtectedCoachRoute><SignUp /></ProtectedCoachRoute>} />
          <Route path="/coach/settings" element={<ProtectedCoachRoute><Settings /></ProtectedCoachRoute>} />
          <Route path="/coach/chat" element={<ProtectedCoachRoute><TeamChat /></ProtectedCoachRoute>} />
          <Route path="/coach/albums" element={<ProtectedCoachRoute><Albums /></ProtectedCoachRoute>} />

          <Route path="/parent/dashboard" element={<ProtectedParentRoute><ParentDashboard /></ProtectedParentRoute>} />
          <Route path="/parent/profile" element={<ProtectedParentRoute><ParentProfile /></ProtectedParentRoute>} />
          <Route path="/parent/children" element={<ProtectedParentRoute><ParentChildren /></ProtectedParentRoute>} />
          <Route path="/parent/schedule" element={<ProtectedParentRoute><ParentSchedule /></ProtectedParentRoute>} />
          <Route path="/parent/payments" element={<ProtectedParentRoute><PaidOnly dashboard="/parent/dashboard"><ParentPayments /></PaidOnly></ProtectedParentRoute>} />
          <Route path="/parent/messages" element={<ProtectedParentRoute><ParentMessages /></ProtectedParentRoute>} />
          <Route path="/parent/waiver" element={<ProtectedParentRoute><ParentWaiver /></ProtectedParentRoute>} />
          <Route path="/parent/chat" element={<ProtectedParentRoute><TeamChat /></ProtectedParentRoute>} />
          <Route path="/parent/albums" element={<ProtectedParentRoute><Albums /></ProtectedParentRoute>} />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
    </AuthProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
